// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN BRANCHES PAGE                           ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Building2,
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  RefreshCw,
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users
} from 'lucide-react'
import { BranchesAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function BranchesPage() {
  const { hasPermission } = useAdmin()
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [states, setStates] = useState([])

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingBranch, setEditingBranch] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [availableManagers, setAvailableManagers] = useState([])

  useEffect(() => {
    fetchData()
    fetchStates()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await BranchesAdminAPI.getBranches()
      if (res.success) {
        setBranches(res.data.branches)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load branches')
    } finally {
      setLoading(false)
    }
  }

  const fetchStates = async () => {
    try {
      const res = await BranchesAdminAPI.getStates()
      if (res.success) {
        setStates(res.data.states)
      }
    } catch (err) {
      console.error('Failed to load states')
    }
  }

  const fetchAvailableManagers = async () => {
    try {
      const res = await BranchesAdminAPI.getAvailableManagers()
      if (res.success) {
        setAvailableManagers(res.data.managers)
      }
    } catch (err) {
      console.error('Failed to load managers')
    }
  }

  const handleSaveBranch = async (branchData) => {
    setProcessing(true)
    try {
      const res = await BranchesAdminAPI.saveBranch(branchData)
      if (res.success) {
        fetchData()
        setShowModal(false)
        setEditingBranch(null)
      }
    } catch (err) {
      console.error('Failed to save branch')
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteBranch = async (branchId) => {
    if (!confirm('Are you sure you want to delete this branch?')) return

    try {
      const res = await BranchesAdminAPI.deleteBranch(branchId)
      if (res.success) {
        fetchData()
      }
    } catch (err) {
      console.error('Failed to delete branch')
    }
  }

  const handleUpdateStatus = async (branchId, newStatus) => {
    try {
      const res = await BranchesAdminAPI.updateStatus(branchId, newStatus)
      if (res.success) {
        fetchData()
      }
    } catch (err) {
      console.error('Failed to update status')
    }
  }

  const formatPhone = (phone) => {
    if (!phone) return '-'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
  }

  const filteredBranches = branches.filter(branch => {
    const matchesSearch = !search ||
      branch.name.toLowerCase().includes(search.toLowerCase()) ||
      branch.code?.toLowerCase().includes(search.toLowerCase()) ||
      branch.city?.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = !statusFilter || branch.status === statusFilter
    const matchesState = !stateFilter || branch.state === stateFilter
    return matchesSearch && matchesStatus && matchesState
  })

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400',
      inactive: 'bg-slate-500/20 text-slate-400',
      temporarily_closed: 'bg-yellow-500/20 text-yellow-400',
      permanently_closed: 'bg-red-500/20 text-red-400'
    }
    return styles[status] || 'bg-slate-500/20 text-slate-400'
  }

  const openAddModal = async () => {
    await fetchAvailableManagers()
    setEditingBranch(null)
    setShowModal(true)
  }

  const openEditModal = async (branch) => {
    await fetchAvailableManagers()
    setEditingBranch(branch)
    setShowModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Branches</h1>
          <p className="text-slate-400 mt-1">Manage bank branch locations</p>
        </div>
        {hasPermission('branches.edit') && (
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Branch
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Branches</p>
              <p className="text-2xl font-bold text-white">{branches.length}</p>
            </div>
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-emerald-400">
                {branches.filter(b => b.status === 'active').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Temporarily Closed</p>
              <p className="text-2xl font-bold text-yellow-400">
                {branches.filter(b => b.status === 'temporarily_closed').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">States Covered</p>
              <p className="text-2xl font-bold text-white">
                {new Set(branches.map(b => b.state).filter(Boolean)).size}
              </p>
            </div>
            <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-violet-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by name, code, or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="temporarily_closed">Temporarily Closed</option>
            <option value="permanently_closed">Permanently Closed</option>
          </select>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All States</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <button
            onClick={fetchData}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-slate-400">{error}</p>
            </div>
          </div>
        ) : filteredBranches.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400">
            No branches found
          </div>
        ) : (
          filteredBranches.map((branch) => (
            <motion.div
              key={branch.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-xl p-5"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{branch.name}</h3>
                    <p className="text-slate-400 text-sm font-mono">{branch.code}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadge(branch.status)}`}>
                  {branch.status?.replace('_', ' ')}
                </span>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div className="text-slate-400">
                    {branch.address && <p>{branch.address}</p>}
                    <p>{branch.city}, {branch.state} {branch.zip_code}</p>
                  </div>
                </div>
                {branch.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-400">{formatPhone(branch.phone)}</span>
                  </div>
                )}
                {branch.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-400">{branch.email}</span>
                  </div>
                )}
                {branch.manager_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-400">Manager: {branch.manager_name}</span>
                  </div>
                )}
              </div>

              {branch.hours && (
                <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Clock className="w-4 h-4" />
                    <span>Hours</span>
                  </div>
                  <p className="text-slate-300 text-sm">{branch.hours}</p>
                </div>
              )}

              {hasPermission('branches.edit') && (
                <div className="flex gap-2 pt-4 border-t border-slate-700">
                  <select
                    value={branch.status}
                    onChange={(e) => handleUpdateStatus(branch.id, e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="temporarily_closed">Temporarily Closed</option>
                    <option value="permanently_closed">Permanently Closed</option>
                  </select>
                  <button
                    onClick={() => openEditModal(branch)}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBranch(branch.id)}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Branch Modal */}
      <AnimatePresence>
        {showModal && (
          <BranchFormModal
            branch={editingBranch}
            managers={availableManagers}
            onClose={() => {
              setShowModal(false)
              setEditingBranch(null)
            }}
            onSave={handleSaveBranch}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Branch Form Modal Component
function BranchFormModal({ branch, managers, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    name: branch?.name || '',
    code: branch?.code || '',
    address: branch?.address || '',
    city: branch?.city || '',
    state: branch?.state || '',
    zip_code: branch?.zip_code || '',
    phone: branch?.phone || '',
    email: branch?.email || '',
    hours: branch?.hours || 'Mon-Fri: 9AM-5PM, Sat: 9AM-1PM',
    manager_id: branch?.manager_id || '',
    status: branch?.status || 'active'
  })

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ]

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      id: branch?.id
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {branch ? 'Edit Branch' : 'Add Branch'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Branch Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Main Street Branch"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Branch Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="BR001"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="New York"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                >
                  <option value="">Select</option>
                  {usStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="10001"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="branch@securebank.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Hours</label>
              <input
                type="text"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="Mon-Fri: 9AM-5PM"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Manager</label>
                <select
                  value={formData.manager_id}
                  onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">No Manager</option>
                  {managers.map(manager => (
                    <option key={manager.id} value={manager.id}>
                      {manager.first_name} {manager.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="temporarily_closed">Temporarily Closed</option>
                  <option value="permanently_closed">Permanently Closed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Branch
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
